import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableVirtuoso } from 'react-virtuoso';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Box, Divider, Typography } from '@mui/material';
import { dataConstants } from '../../../constants';
import { formatTableValue } from '../../../utils';
import { formatCount } from '../../../utils/D3/helper';
import { roundNum } from '../../../helpers';
import _getLatestPrice from '../../../../pages/api/_getLatestPrice';
import useSWR from 'swr';

const convertData = (data) => {
  const result = data.reduce((acc, { ticker, r, p }) => {
    if (!acc[ticker]) {
      acc[ticker] = { ticker, price_data: [] };
    }
    acc[ticker].price_data.push({ price: p });
    return acc;
  }, {});
  return result;
};

const calcPercent = (a, b) => {
  const percent = roundNum((a - b) * 100 / b, 2);
  return percent >= 0 ? `+${percent}` : percent;
};

const CellTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'rgba(22,27,87,0.92)',
    fontWeight: 600,
    color: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    fontSize: 11,
    borderRadius: '6px',
    padding: '5px 12px',
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  overflow: 'hidden',
  marginTop: '16px',
}));

const PctCell = ({ value, mult = 1 }) => {
  const num = value ? roundNum(value * mult) : null;
  if (num === null || isNaN(num)) {
    return <Typography variant="caption" sx={{ color: '#999' }}>&mdash;</Typography>;
  }
  const isPositive = num >= 0;
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'inline-block',
        px: 0.75,
        py: 0.15,
        borderRadius: '5px',
        fontWeight: 600,
        fontSize: '.77rem',
        backgroundColor: isPositive ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)',
        color: isPositive ? '#059669' : '#dc2626',
      }}
    >
      {num}%
    </Typography>
  );
};

const columns = [
  { label: "Ticker", tooltip: "Ticker", field: "ticker", urlField: "ticker", align: "left", width: 70, fontWeight: 600 },
  { label: "Name", tooltip: "Name", field: "name", urlField: "ticker", align: "left", width: 135, fontWeight: 500 },
  { label: "Industry", field: "industry", urlField: "industry", align: "left", isSectorValue: false, isPct: false, mult: 1, tooltip: "Industry", width: 135, fontWeight: 500 },
  { label: "Price", field: "price", align: "right", isPrice: true, width: 80, fontWeight: 500 },
  { label: "Mkt. Cap.", field: "MKTCAP", align: "right", isSectorValue: true, isPct: false, mult: 1, tooltip: "Market Capital", width: 87 },
  { label: "Rev. TTM", field: "REV_TTM", align: "right", isSectorValue: true, isPct: false, mult: 1, tooltip: "Net sales", width: 87 },
  { label: "1D Pct.", field: "PCHG__1", align: "right", isSectorValue: false, isPct: true, mult: 1, tooltip: "1-Day Price % Change", width: 77 },
  { label: "1W Pct.", field: "PCHG__7", align: "right", isSectorValue: false, isPct: true, mult: 100, tooltip: "1-Week Price % Change", width: 77 },
  { label: "1M Pct.", field: "PCHG__30", align: "right", isSectorValue: false, isPct: true, mult: 100, tooltip: "1-Month Price % Change", width: 77 },
  { label: "6M Pct.", field: "PCHG__180", align: "right", isSectorValue: false, isPct: true, mult: 100, tooltip: "6-Month Price % Change", width: 77 },
  { label: "1Y Pct.", field: "PCHG__365", align: "right", isSectorValue: false, isPct: true, mult: 100, tooltip: "1-Year Price % Change", width: 77 },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer
      {...props}
      ref={ref}
      sx={{
        '&::-webkit-scrollbar': { width: '4px !important', height: '4px !important' },
        '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
        '&:hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}
    />
  )),
  Table: (props) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
  TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

function fixedHeaderContent() {
  return (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.field}
          variant="head"
          align={column.align}
          sx={{
            backgroundColor: '#fff',
            fontWeight: 700,
            fontSize: '0.7rem',
            color: 'rgb(100,116,139)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            pt: 1.5,
            pb: 1,
            width: column.width,
            whiteSpace: 'nowrap',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  );
}

function rowContent(_index, row) {
  return (
    <React.Fragment>
      {columns.map((column) => (
        <TableCell
          key={column.field}
          align={column.align}
          sx={{
            pt: 0.7,
            pb: 0.5,
            width: column.width,
            '& .ellipsis': { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' },
            borderBottom: '1px solid rgba(0,0,0,0.04)',
          }}
        >
          {column.urlField ? (
            <CellTooltip title={column.urlField === "ticker" ? row['name'] : ""} placement="bottom-end" followCursor disableInteractive>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={column.urlField === 'ticker'
                  ? `${dataConstants.NAVS.STOCK}/${row[column.urlField]}`
                  : `/sectors/industry/${row[column.urlField]?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}`}
                style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: '#174ea6' }}
              >
                <Typography variant="caption" className="ellipsis" sx={{ fontWeight: column.fontWeight, '&:hover': { textDecoration: 'underline' } }}>
                  {row[column.field] ? formatTableValue(row[column.field]) : '\u2014'}
                </Typography>
              </a>
            </CellTooltip>
          ) : column.isSectorValue ? (
            <CellTooltip title={column.tooltip} placement="bottom-end" followCursor disableInteractive>
              <Typography variant="caption" component="div" className="ellipsis">
                {row[column.field] ? formatCount(Math.sign(row[column.field]) * row[column.field], true, 1, Math.sign(row[column.field])) : '\u2014'}
              </Typography>
            </CellTooltip>
          ) : column.isPct ? (
            <CellTooltip title={column.tooltip} placement="bottom-end" followCursor disableInteractive>
              <span><PctCell value={row[column.field]} mult={column.mult} /></span>
            </CellTooltip>
          ) : column.isPrice ? (
            <Typography variant="caption">{row[column.field] ? `$${row[column.field]}` : '\u2014'}</Typography>
          ) : (
            <Typography variant="caption" component="div" className="ellipsis">
              {row[column.field] ? formatTableValue(row[column.field]) : '\u2014'}
            </Typography>
          )}
        </TableCell>
      ))}
    </React.Fragment>
  );
}

const getPCHG_1 = (sectorMetrics) => {
  const { data, error, isValidating } = useSWR(
    ["api/_getLatestPrice", sectorMetrics.map(d => d.ticker)],
    _getLatestPrice,
    {
      refreshInterval: 180000,
      revalidateOnFocus: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (error.status === 404 || key === 'api/_getLatestPrice' || retryCount >= 10) return;
        setTimeout(() => revalidate({ retryCount }), 180000);
      }
    }
  ) || {};

  const { getLatestPrice: latestPrices } = data || {};

  const mergedData = React.useMemo(() => {
    if (!latestPrices) return sectorMetrics;
    const priceData = convertData(latestPrices);
    return sectorMetrics.map(item => {
      const tickerData = priceData[item.ticker];
      if (tickerData && tickerData.price_data.length >= 2) {
        const [latestPrice, previousPrice] = tickerData.price_data;
        item.PCHG__1 = calcPercent(latestPrice.price, previousPrice.price);
        item.CHG__1 = roundNum(latestPrice.price - previousPrice.price);
        item.price = latestPrice.price;
        item.updatedAt = new Date();
      } else {
        item.PCHG__1 = null;
        item.CHG__1 = null;
      }
      return item;
    });
  }, [latestPrices]);

  return { data: mergedData, error, isValidating };
};

export default function HeatMapTable({ sectorMetrics, selectedSector }) {
  const { data, error, isValidating } = getPCHG_1(sectorMetrics);

  return (
    <Card>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography variant="h5" component="h5" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary', mb: 0.25 }}>
          {`Top 25 Largest Companies${selectedSector ? ' in ' + selectedSector : ''}`}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgb(100,116,139)', fontSize: '.82rem' }}>
          Ranked by market capitalization
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />
      <Box sx={{
        height: 400,
        width: '100%',
        '& .MuiTableRow-root:hover': { backgroundColor: 'rgba(22,27,87,0.02)' },
        '& a': { textDecoration: 'none', color: '#174ea6' },
      }}>
        <TableVirtuoso
          data={data}
          isLoading={isValidating}
          components={VirtuosoTableComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={rowContent}
        />
      </Box>
    </Card>
  );
}
